/**
 * BulkTagsModal — add tags to, and remove tags from, every selected item in
 * one pass. Tags to add are merged into each item's own list; tags to remove
 * are offered from what the selection already carries, with a count of how
 * many items carry each.
 */
import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { Modal, TextInput, Checkbox, Callout } from '@/components/ds';
import { Button } from '@/components/ui/Button';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import type { Item } from '../../../types/prioritization/lifecycle';
import { itemsWord, runBulk, type BulkActionResult } from './index';

export interface BulkTagsModalProps {
  open: boolean;
  selectedIds: string[];
  onClose: () => void;
  onComplete: (result: BulkActionResult) => void;
}

const normalize = (raw: string) => raw.trim().toLowerCase();

export function BulkTagsModal({ open, selectedIds, onClose, onComplete }: BulkTagsModalProps) {
  const [toAdd, setToAdd] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [toRemove, setToRemove] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const items = useItemsStore((s) => s.items);
  const updateItem = useItemsStore((s) => s.updateItem);

  const selected = useMemo(
    () => selectedIds.map((id) => items.find((i) => i.id === id)).filter((i): i is Item => !!i),
    [items, selectedIds],
  );

  /** Every tag the selection carries, with how many of the selected items carry it. */
  const present = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of selected) for (const t of item.tags ?? []) counts.set(t, (counts.get(t) ?? 0) + 1);
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [selected]);

  const commitDraft = () => {
    const next = draft
      .split(',')
      .map(normalize)
      .filter((t) => t && !toAdd.includes(t));
    if (next.length) setToAdd((prev) => [...prev, ...next]);
    setDraft('');
  };

  const count = selectedIds.length;
  const removedCarriers = present
    .filter(([tag]) => toRemove.has(tag))
    .reduce((n, [, c]) => Math.max(n, c), 0);
  const nothingToDo = toAdd.length === 0 && toRemove.size === 0;

  const apply = async () => {
    if (nothingToDo || busy) return;
    setBusy(true);
    try {
      const result = await runBulk(
        'Tags changed',
        selectedIds,
        (id) => items.find((i) => i.id === id)?.title ?? id,
        async (id) => {
          const item = items.find((i) => i.id === id);
          if (!item) return { ok: false, reason: 'Item not found' };
          const current = item.tags ?? [];
          const next = Array.from(new Set([...current.filter((t) => !toRemove.has(t)), ...toAdd]));
          const unchanged = next.length === current.length && next.every((t) => current.includes(t));
          if (unchanged) return { ok: true };
          return updateItem(id, { tags: next });
        },
        (done, total) => setProgress({ done, total }),
      );
      onComplete(result);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => { if (!busy) onClose(); }}
      dismissible={!busy}
      size="md"
      title={`Tags for ${itemsWord(count)}`}
      description="Tags you add are merged into each item's own tags. Tags you remove come off every selected item that carries them."
      footer={
        <>
          <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" variant="cta" size="sm" onClick={() => void apply()} disabled={busy || nothingToDo}>
            {busy && progress ? `Saving… ${progress.done} of ${progress.total}` : `Apply to ${itemsWord(count)}`}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <p className="mb-1.5 font-mono text-[0.72rem] uppercase tracking-[0.12em] text-[var(--text-ter)]">Add tags</p>
          <TextInput
            size="sm"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commitDraft(); }
              if (e.key === 'Backspace' && !draft && toAdd.length) setToAdd((prev) => prev.slice(0, -1));
            }}
            onBlur={commitDraft}
            placeholder="Type a tag and press Enter"
            autoFocus
            disabled={busy}
          />
          {toAdd.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {toAdd.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 border bg-[var(--mustard-dim)] px-2 py-0.5 font-mono text-[0.74rem] uppercase tracking-[0.08em] text-[var(--mustard)]"
                  style={{ borderColor: 'var(--mustard)' }}
                >
                  {tag}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="!p-0 !text-current"
                    aria-label={`Don't add ${tag}`}
                    onClick={() => setToAdd((prev) => prev.filter((t) => t !== tag))}
                    disabled={busy}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </span>
              ))}
            </div>
          )}
        </div>

        {present.length > 0 && (
          <div>
            <p className="mb-1.5 font-mono text-[0.72rem] uppercase tracking-[0.12em] text-[var(--text-ter)]">Remove tags</p>
            <div className="max-h-40 overflow-y-auto border" style={{ borderColor: 'var(--rule)' }}>
              {present.map(([tag, n]) => (
                <div key={tag} className="border-b px-3 py-1.5 last:border-b-0" style={{ borderColor: 'var(--rule)' }}>
                  <Checkbox
                    checked={toRemove.has(tag)}
                    disabled={busy}
                    onChange={() =>
                      setToRemove((prev) => {
                        const next = new Set(prev);
                        if (next.has(tag)) next.delete(tag); else next.add(tag);
                        return next;
                      })
                    }
                    label={tag}
                    description={`on ${n} of ${count}`}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {!nothingToDo && (
          <Callout tone="slate">
            You are about to{' '}
            {toAdd.length > 0 && (
              <>add {toAdd.length} tag{toAdd.length === 1 ? '' : 's'} to {itemsWord(count)}</>
            )}
            {toAdd.length > 0 && toRemove.size > 0 && ' and '}
            {toRemove.size > 0 && (
              <>remove {toRemove.size} tag{toRemove.size === 1 ? '' : 's'} from the {removedCarriers} that carry {toRemove.size === 1 ? 'it' : 'them'}</>
            )}
            .
          </Callout>
        )}
      </div>
    </Modal>
  );
}
